import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { Input, Spinner, Toggle } from '@/components/ui'
import Contact from "../Contact"

import { 
  createMandatWithProprietaires, 
  updateMandatWithProprietaires,
  selectMandatWithProprietairesLoading,
  deleteMandat
} from '../../mandats/mandatsSlice'
import { updateBien } from '../biensSlice'
import { useBienContext } from '../BienContext'
import ProprietaireFields from './ProprietaireFields'
import MandatFields from './MandatFields'

export default ({ mode, onClose, onSuccess }) => {
  const dispatch = useDispatch()
  const { bien } = useBienContext()
  const mandat = useSelector(state => state.mandats.inEdition)
  const isLoading = useSelector(selectMandatWithProprietairesLoading)
  const errors = useSelector( state => state.mandats.requestStatus.errors)
  const bienLoading = useSelector(state => state.biens.loadingStatus.update)
  const bienErrors = useSelector(state => state.biens.requestStatus.errors)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [ showConfirmDialog, setShowConfirmDialog ] = useState(false)

  const { handleSubmit, setValue, control, watch, formState: { errors: formErrors } } = useForm({
    defaultValues: {
      ...mandat,
      proprietaires: bien?.proprietaires || [],
      cle_dispo: bien?.cle_dispo || false
    }
  })

  const shouldUpdateProprietaires = mode === 'new_owner' || 
                                (mode === 'edit_mandat' && !mandat?.date_signature)

  useEffect(() => {
    if(mandat) {
      Object.keys(mandat).forEach(key => {
        if (key in mandat) {
          setValue(key, mandat[key])
        }
      })
    }
    if(shouldUpdateProprietaires && bien?.proprietaires?.length > 0) {
      setValue("proprietaires", bien?.proprietaires || [])
    }
  }, [bien?.proprietaires, mandat, mode])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isLoading && !bienLoading) {
      setIsSubmitting(false)
      const hasErrors = (errors && Object.keys(errors).length > 0) || 
                        (bienErrors && Object.keys(bienErrors).length > 0)
      if (!hasErrors) {
        onSuccess()
      }
    }
  }, [isLoading, bienLoading, errors, bienErrors, isSubmitting, onSuccess, dispatch])

  const onSubmit = async (formData) => {
    const mandatPayload = {
      bien_id: bien.id,
      mandat: {
        type_mandat: formData.type_mandat,
        numero: formData.numero,
        date_signature: formData.date_signature,
        exclusivite: formData.exclusivite,
        mandat_file: formData.mandat_file
      }
    }
    const bienPayload = {
      cle_dispo: formData.cle_dispo,
      remise_cles_file: formData.remise_cles_file
    }

    if (shouldUpdateProprietaires) {
      // Cas : Arrivée de nouveaux propriétaires
      mandatPayload.proprietaires = formData.proprietaires.map(p => ({ 
        contact_id: p.id || p.contact_id 
      }))
    }

    try {
      setIsSubmitting(true)

      // Étape 1 : Créer/mettre à jour le mandat
      if (mandat?.id) {
        await dispatch(updateMandatWithProprietaires({ id: mandat.id, ...mandatPayload }))
      } else {
        await dispatch(createMandatWithProprietaires(mandatPayload))
      }
      // Étape 2 : Mettre à jour le bien (fichiers et cle_dispo)
      if (bienPayload.remise_cles_file || bienPayload.cle_dispo !== bien.cle_dispo) {
        await dispatch(updateBien({ 
          id: bien.id, 
          bienData: bienPayload 
        }))
      }

    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      setIsSubmitting(false)
    }
  }

  const handleDelete = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setShowConfirmDialog(true)
  }

  const confirmDelete = async () => {
    setIsSubmitting(true)
    await dispatch(deleteMandat({bien_id: bien.id, id: mandat.id}))
    setIsSubmitting(false)
    setShowConfirmDialog(false)
    onSuccess()
  }
  
  const cancelDelete = () => {
    setShowConfirmDialog(false)
  }

  const getFormTitle = () => {
    switch (mode) {
      case 'new_owner':
        return 'Arrivée de nouveaux propriétaires'
      case 'edit_mandat':
        return `Modifier mandat ${mandat?.type_mandat || ''}`
      case 'add_mandat':
        return `Ajouter mandat ${mandat?.type_mandat || ''}`
      default:
        return 'Gestion du mandat'
    }
  }

  const showProprietaireFields = () => {
    // Afficher les champs propriétaires dans ces cas :
    // 1. Mode "arrivée de nouveaux propriétaires"
    // 2. Mode "édition mandat" ET mandat non signé (permet modification propriétaires)
    return mode === 'new_owner' ||
           (mode === 'edit_mandat' && !mandat?.date_signature)
  }
  
  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  const typeMandat = watch("type_mandat")

  return (
    <div>
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
      <h2 className="text-2xl font-bold">{getFormTitle()}</h2>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="space-y-4">
          {/* Section Propriétaires */}
          {showProprietaireFields() && (
            <div className="space-y-4">
              <ProprietaireFields
                control={control}
                formErrors={formErrors}
              />
            </div>
          )}

          {mode === 'add_mandat' && bien?.proprietaires?.length > 0 && (
            <div className="space-y-4">
              {bien.proprietaires.map((proprietaire, index) => (
                <Contact key={proprietaire.id} label="Propriétaire" item={proprietaire} index={index} />
              ))}
            </div>
          )}

          <Toggle.Form 
            control={control} 
            name="cle_dispo" 
            label="Clé disponible à l'agence" 
            className="w-full max-w-md"
          />

          <Input.File control={control}
            name="remise_cles_file"
            label="Remise de clé"
            defaultValue={bien?.remise_cles_file?.filename || '' }
            inputProps={{accept: ".pdf"}}
            canDelete={true}
          />
        </div>

        {/* Section Mandat */}
        <div className="space-y-4">
          <MandatFields 
            control={control} 
            mandat={mandat}
            readOnlyType={mode === 'add_mandat'}
            validator={{hasError, getErrorMessage}}
          />
        </div>

        <div className="space-y-4">
          { typeMandat === "vente" && 
            <div className="flex flex-col items-center gap-4">  
              <Toggle.Form
                control={control}
                name="exclusivite"
                label="Exclusivité"
              />
            </div>
          }
        </div>
      </div>

      <div className="flex justify-between pt-4">
        <button type="button" className="btn btn-outline" onClick={onClose}>
          Annuler
        </button>

        <div className="space-x-2">
          {/* { (mandat?.id && !mandat?.date_signature) && ( */}
          { mandat?.id && (
            <button
                className={`btn btn-secondary text-white`}
                disabled={isLoading}
                onClick={handleDelete}
              >
              Supprimer
            </button>
          )}
          <button 
            type="submit" 
            className={`btn btn-primary ${isLoading ? 'loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? <Spinner /> : 'Enregistrer'}
          </button>
        </div>

      </div>
    </form>
    {(showConfirmDialog && mandat) && (
      <dialog open className="modal">
        <div className="modal-box">
          <h3 className="font-bold text-lg">Confirmer la suppression</h3>
          <p className="py-4">Êtes-vous sûr de vouloir supprimer ce mandat ?</p>
          <p className="py-4 text-center">
            Mandat de <span className="capitalize">{ mandat.type_mandat }</span>
            {mandat.numero && <span> n° {mandat.numero}</span>}
          </p>
          <div className="modal-action">
            <button onClick={confirmDelete} className="btn btn-secondary">Supprimer</button>
            <button onClick={cancelDelete} className="btn btn-outline">Annuler</button>
          </div>
        </div>
      </dialog>
    )}
    </div>
  )
}
